import { useState, type Ref } from "react";
import { useQuery } from "@tanstack/react-query";
import { Play, ChevronDown, ChevronRight } from "lucide-react";

import {
  buildRecordingStreamUrl,
  fetchRecordingAccessLog,
} from "../../api/calls";

type Props = {
  callId: string;
  callState: "LIVE" | "ENDED" | "HALTED";
  revealed: boolean;
  onReveal: () => void;
  audioRef: Ref<HTMLAudioElement>;
};

/**
 * Recording playback panel. Points an HTML <audio> element at the
 * console-api streaming endpoint with `crossOrigin="use-credentials"`,
 * which forwards the operator's session cookie on every byte-range
 * request. No presigned URL is involved — when the session expires,
 * the next range request 401s and playback stops automatically.
 *
 * The audio element's ref is owned by Detail.tsx so the AudioScrubber
 * can jump to a turn's offset by setting currentTime + play().
 *
 * Hidden during LIVE state — the egress hasn't finalized.
 */
export function Recording({ callId, callState, revealed, onReveal, audioRef }: Props) {
  const [showAccessLog, setShowAccessLog] = useState(false);

  const accessLog = useQuery({
    queryKey: ["recording-access-log", callId],
    queryFn: () => fetchRecordingAccessLog(callId),
    enabled: showAccessLog && callState !== "LIVE",
  });

  // Hide entirely while the call is live — recording isn't finished yet.
  if (callState === "LIVE") return null;

  const streamUrl = buildRecordingStreamUrl(callId);

  return (
    <div
      data-testid="recording-section"
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        overflow: "hidden",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 18px",
          borderBottom: "1px solid var(--border-subtle)",
          background: "var(--bg-secondary)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--text-primary)",
        }}
      >
        <span>Recording</span>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 400,
            color: "var(--text-muted)",
          }}
          title="Each request is authenticated by your session cookie. When your session expires, playback stops automatically."
        >
          Session-authenticated stream
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
        {revealed ? (
          <LoadedPlayer src={streamUrl} audioRef={audioRef} />
        ) : (
          <button
            type="button"
            data-testid="play-recording-button"
            onClick={onReveal}
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 18px",
              borderRadius: "var(--radius-input)",
              background: "var(--accent)",
              color: "#FFFFFF",
              border: "1px solid var(--accent)",
              fontFamily: "var(--font-sans)",
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Play size={13} strokeWidth={1.75} fill="currentColor" />
            Play recording
          </button>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            paddingTop: 12,
            borderTop: "1px dashed var(--border-subtle)",
          }}
        >
          <button
            type="button"
            data-testid="toggle-access-log"
            onClick={() => setShowAccessLog((s) => !s)}
            style={{
              alignSelf: "flex-start",
              background: "transparent",
              border: "none",
              color: "var(--text-muted)",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
              fontSize: 12.5,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {showAccessLog ? (
              <ChevronDown size={12} strokeWidth={1.5} />
            ) : (
              <ChevronRight size={12} strokeWidth={1.5} />
            )}
            Access history
          </button>

          {showAccessLog && accessLog.data ? (
            <AccessLogTable rows={accessLog.data.items} />
          ) : showAccessLog && accessLog.isPending ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Loading access log…
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LoadedPlayer({ src, audioRef }: { src: string; audioRef: Ref<HTMLAudioElement> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* crossOrigin="use-credentials" sends the session cookie on every
          byte-range request. Required because the SPA host (ops.angler...)
          and the API host (api.angler...) are cross-origin subdomains. */}
      <audio
        ref={audioRef}
        data-testid="recording-audio"
        controls
        crossOrigin="use-credentials"
        src={src}
        style={{
          width: "100%",
          borderRadius: "var(--radius-input)",
        }}
      />
      <span
        style={{
          fontSize: 11.5,
          color: "var(--text-muted)",
          fontFamily: "var(--font-sans)",
        }}
      >
        Streaming via console-api — playback ends if your session expires.
      </span>
    </div>
  );
}

function AccessLogTable({
  rows,
}: {
  rows: ReadonlyArray<{ operator_email: string; accessed_at: string; ttl_seconds: number }>;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
        No prior accesses.
      </div>
    );
  }
  return (
    <div
      data-testid="access-log-table"
      style={{
        display: "flex",
        flexDirection: "column",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-input)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr 0.5fr",
          gap: 12,
          padding: "8px 12px",
          fontFamily: "var(--font-sans)",
          fontSize: 11.5,
          color: "var(--text-muted)",
          background: "var(--bg-tertiary)",
          borderBottom: "1px solid var(--border-subtle)",
          fontWeight: 500,
        }}
      >
        <span>Operator</span>
        <span>Accessed</span>
        <span style={{ textAlign: "right" }}>Window</span>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "1.5fr 1fr 0.5fr",
            gap: 12,
            padding: "8px 12px",
            fontSize: 12.5,
            color: "var(--text-secondary)",
            borderBottom: i === rows.length - 1 ? "none" : "1px dashed var(--border-subtle)",
          }}
        >
          <span style={{ color: "var(--text-primary)" }}>{r.operator_email}</span>
          <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {r.accessed_at}
          </span>
          <span
            style={{
              color: "var(--text-muted)",
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              fontFamily: "var(--font-mono)",
            }}
          >
            {Math.round(r.ttl_seconds / 60)}m
          </span>
        </div>
      ))}
    </div>
  );
}
